<!-- This is a global error page for Nuxt that will catch all errors -->
<template>
  <div class="error-page">
    <div class="error-container">
      <h1>{{ errorMessage }}</h1>
      
      <p v-if="error?.statusCode === 404">
        The page you are looking for doesn't exist or has been moved.
      </p>
      
      <p v-else>
        Sorry, something went wrong. Our team has been notified.
      </p>
      
      <!-- Show detailed error information in development mode -->
      <div v-if="showDetails && error?.details" class="error-details">
        <h3>Error Details:</h3>
        <div v-if="error.details.type === 'NuxtInstanceError'" class="nuxt-error">
          <p><strong>Type:</strong> {{ error.details.type }}</p>
          <p><strong>Hint:</strong> {{ error.details.hint }}</p>
          <p><strong>Possible Fix:</strong> {{ error.details.possibleFix }}</p>
        </div>
        
        <div v-if="error.stack && error.stack.length > 0" class="stack-trace">
          <h4>Stack Trace:</h4>
          <pre><code>{{ error.stack.join('\n') }}</code></pre>
        </div>
      </div>
      
      <div class="actions">
        <button @click="handleError" class="primary-button">
          Go back home
        </button>
        <button 
          v-if="error?.statusCode === 500" 
          @click="toggleDetails" 
          class="secondary-button"
        >
          {{ showDetails ? 'Hide' : 'Show' }} Details
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
const error = useError();
const showDetails = ref(process.env.NODE_ENV === 'development');

const errorMessage = computed(() => {
  if (error.value?.statusCode === 404) {
    return 'Page Not Found';
  }
  
  if (error.value?.details?.type === 'NuxtInstanceError') {
    return 'Nuxt Instance Error';
  }
  
  return error.value?.message || 'An error occurred';
});

function handleError() {
  clearError({ redirect: '/' });
}

function toggleDetails() {
  showDetails.value = !showDetails.value;
}
</script>

<style scoped>
.error-page {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 2rem;
  background-color: #f8f9fa;
}

.error-container {
  max-width: 800px;
  padding: 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
}

h1 {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #dc3545;
}

.error-details {
  margin: 2rem 0;
  text-align: left;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 4px solid #6c757d;
}

.nuxt-error {
  padding: 1rem;
  background-color: #fff3cd;
  border-radius: 4px;
  margin-bottom: 1rem;
}

.stack-trace {
  overflow-x: auto;
}

pre {
  background-color: #343a40;
  color: #fff;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 0.875rem;
}

.actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
}

button {
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.primary-button {
  background-color: #007bff;
  color: white;
  border: none;
}

.primary-button:hover {
  background-color: #0069d9;
}

.secondary-button {
  background-color: transparent;
  color: #6c757d;
  border: 1px solid #6c757d;
}

.secondary-button:hover {
  background-color: #6c757d;
  color: white;
}
</style> 